import { Helmet } from '../components/Helmet';
import { motion } from 'framer-motion';
import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Search, Calendar, User, Clock } from 'lucide-react';
import { getAllBlogPosts, getAllCategories } from '../utils/blogUtils';

const BlogPage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeCategory, setActiveCategory] = useState("Tous");

  // Load all blog posts from Markdown files
  const allPosts = useMemo(() => getAllBlogPosts(), []);
  const categories = useMemo(() => {
    const cats = getAllCategories();
    return ["Tous", ...cats.map(c => c.name)];
  }, []);

  // Filter posts based on search term and category
  const filteredPosts = useMemo(() => {
    return allPosts.filter(post => {
      if (!post.title || !post.excerpt) return false;
      const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());

      let matchesCategory = activeCategory === "Tous";
      if (!matchesCategory && post.category) {
        const postCategories = Array.isArray(post.category) ? post.category : [post.category];
        matchesCategory = postCategories.includes(activeCategory);
      }

      return matchesSearch && matchesCategory;
    });
  }, [allPosts, searchTerm, activeCategory]);

  // Separate featured and regular posts, sorted by date (newest first)
  const sortedFilteredPosts = useMemo(() => {
    return [...filteredPosts].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [filteredPosts]);

  const featuredPosts = sortedFilteredPosts.filter(post => post.featured);
  const regularPosts = sortedFilteredPosts.filter(post => !post.featured);

  return (
    <>
      <Helmet>
        <title>Blog IA Entreprise — Stratégie, Formation et Cas d'usage</title>
        <meta
          name="description"
          content="Ressources et articles experts sur l'IA en entreprise : stratégie IA, formation des équipes, cas d'usage métiers, RGPD et déploiement opérationnel. Par Upnov."
        />
        <meta name="keywords" content="blog IA entreprise, intelligence artificielle entreprise, cas usage IA, formation IA, stratégie IA" />
        <meta property="og:title" content="Blog IA Entreprise — Stratégie, Formation et Cas d'usage" />
        <meta property="og:description" content="Ressources et articles experts sur l'IA en entreprise : stratégie IA, formation des équipes, cas d'usage métiers, RGPD et déploiement opérationnel. Par Upnov." />
        <meta property="og:url" content="https://www.upnov.com/blog" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.upnov.com/blogimg/team-building-ia.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-primary text-white py-16 md:py-24">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="heading-lg mb-6">Blog IA pour entreprises</h1>
            <p className="text-xl mb-8">
              Stratégie, formation, cas d'usage et déploiement de l'IA en entreprise :
              nos articles experts pour accélérer votre transformation.
            </p>

            {/* Search Bar */}
            <div className="relative max-w-xl mx-auto">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Rechercher un article..."
                className="bg-white bg-opacity-10 text-white placeholder-gray-300 w-full py-3 pl-10 pr-4 rounded-lg border border-white border-opacity-20 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-40 transition-colors"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-8 border-b border-gray-200">
        <div className="container-custom">
          <div className="flex flex-wrap items-center justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-4 py-2 rounded-full transition-colors ${activeCategory === category
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Posts Section */}
      {featuredPosts.length > 0 && (
        <section className="section-padding bg-white">
          <div className="container-custom">
            <h2 className="heading-md mb-12 text-center">Articles à la une</h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {featuredPosts.map((post, index) => (
                <motion.article
                  key={post.slug}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-gray-50 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 h-full">
                    <div className="relative overflow-hidden">
                      <img
                        src={post.image}
                        alt={post.title}
                        width="600"
                        height="400"
                        className="w-full h-full object-cover"
                      />
                      {post.category && (
                        <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                          {(Array.isArray(post.category) ? post.category : [post.category]).map((cat) => (
                            <div key={cat} className="bg-primary text-white text-xs font-medium py-1 px-3 rounded-full">
                              {cat}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="p-6 flex flex-col">
                      <h3 className="text-xl font-semibold mb-3">{post.title}</h3>
                      <p className="text-gray-600 mb-4">{post.excerpt}</p>

                      <div className="flex flex-wrap gap-4 mt-auto mb-4">
                        <div className="flex items-center text-sm text-gray-500">
                          <User className="h-4 w-4 mr-1" />
                          <span>{post.author}</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-500">
                          <Calendar className="h-4 w-4 mr-1" />
                          <span>{new Date(post.date).toLocaleDateString('fr-FR')}</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-500">
                          <Clock className="h-4 w-4 mr-1" />
                          <span>{post.readTime} de lecture</span>
                        </div>
                      </div>

                      <Link to={`/blog/${post.slug}/`} className="text-primary font-medium hover:text-primary-light transition-colors flex items-center mt-2">
                        Lire l'article
                        <ChevronRight className="ml-1 h-4 w-4" />
                      </Link>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* All Posts Section */}
      <section className={`section-padding ${featuredPosts.length > 0 ? 'bg-gray-50' : 'bg-white'}`}>
        <div className="container-custom">
          <h2 className="heading-md mb-12 text-center">Tous les articles</h2>

          {regularPosts.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl">
              <p className="text-lg text-gray-600">Aucun article ne correspond à votre recherche.</p>
              <button
                onClick={() => {
                  setSearchTerm("");
                  setActiveCategory("Tous");
                }}
                className="mt-4 text-primary font-medium hover:underline"
              >
                Réinitialiser les filtres
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {regularPosts.map((post, index) => (
                <motion.article
                  key={post.slug}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow h-full flex flex-col"
                >
                  <div className="relative">
                    <img
                      src={post.image}
                      alt={post.title}
                      width="400"
                      height="192"
                      className="w-full h-48 object-cover"
                    />
                    {post.category && (
                      <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                        {(Array.isArray(post.category) ? post.category : [post.category]).map((cat) => (
                          <div key={cat} className="bg-primary text-white text-xs font-medium py-1 px-3 rounded-full">
                            {cat}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="p-6 flex-grow flex flex-col">
                    <h3 className="text-xl font-semibold mb-3">{post.title}</h3>
                    <p className="text-gray-600 mb-4">{post.excerpt}</p>

                    <div className="flex flex-wrap gap-4 mt-auto mb-4">
                      <div className="flex items-center text-sm text-gray-500">
                        <User className="h-4 w-4 mr-1" />
                        <span>{post.author}</span>
                      </div>
                      <div className="flex items-center text-sm text-gray-500">
                        <Calendar className="h-4 w-4 mr-1" />
                        <span>{new Date(post.date).toLocaleDateString('fr-FR')}</span>
                      </div>
                      <div className="flex items-center text-sm text-gray-500">
                        <Clock className="h-4 w-4 mr-1" />
                        <span>{post.readTime} de lecture</span>
                      </div>
                    </div>

                    <Link to={`/blog/${post.slug}/`} className="text-primary font-medium hover:text-primary-light transition-colors flex items-center mt-2">
                      Lire l'article
                      <ChevronRight className="ml-1 h-4 w-4" />
                    </Link>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Subscribe Section */}
      <section className="bg-primary text-white py-16">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="heading-md mb-6">Restez informé</h2>
            <p className="text-xl mb-8">
              Abonnez-vous à notre newsletter pour recevoir nos derniers articles et actualités sur l'IA.
            </p>

            <form className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
              <input
                type="email"
                placeholder="Votre adresse email"
                className="flex-grow px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent transition-colors"
                required
              />
              <button
                type="submit"
                className="btn-accent whitespace-nowrap"
              >
                S'abonner
              </button>
            </form>
            <p className="text-sm mt-4 text-gray-300">
              En vous inscrivant, vous acceptez notre politique de confidentialité. Vous pouvez vous désabonner à tout moment.
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default BlogPage;